import React, { createContext, useContext } from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import useConfigStore from '@/stores/useConfigStore';
import { Auth0UserWithUserType } from './AuthProvider';
import { useAuthErrorFromUrl } from '@/shared/hooks/useAuthErrorFromUrl';
import { AUTH0_SCOPES } from './constants';

interface AuthContextType {
  loadingAuth: boolean;
  signUp: () => Promise<void>;
  logIn: () => Promise<void>;
  auth0Logout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
  isAuthenticated: boolean;
  auth0User?: Auth0UserWithUserType | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface Props {
  children: React.ReactNode;
}

export const BaseAuthProvider = ({ children }: Props) => {
  const {
    isLoading,
    loginWithRedirect,
    logout,
    user,
    isAuthenticated,
    getAccessTokenSilently,
  } = useAuth0();

  const signUp = async () => {
    loginWithRedirect({
      authorizationParams: { screen_hint: 'signup' },
    });
  };

  const logIn = async () => {
    await loginWithRedirect();
  };

  const auth0Logout = async () => {
    await logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const getAccessToken = async () => {
    return await getAccessTokenSilently();
  };

  return (
    <AuthContext.Provider
      value={{
        loadingAuth: isLoading,
        signUp,
        logIn,
        auth0Logout,
        getAccessToken,
        isAuthenticated,
        auth0User: user as Auth0UserWithUserType,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const AuthProvider = ({ children }: Props) => {
  const environment = useConfigStore((state) => state.environment);
  useAuthErrorFromUrl();

  return (
    <Auth0Provider
      domain={environment.auth0.domain}
      clientId={environment.auth0.clientId}
      authorizationParams={{
        audience: environment.auth0.audience,
        redirect_uri: window.location.origin,
        scope: AUTH0_SCOPES,
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <BaseAuthProvider>{children}</BaseAuthProvider>
    </Auth0Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
